import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Overlay, OverlayTrigger, Popover } from "react-bootstrap";
import moment from "moment";

import {
    getCurrentDate,
    getExpiryDateFromDays,
    getFormattedSentDateForDetailedView,
} from "../../../helper/DateHelperFunctions";
import CustomDatePicker from "src/components/Common/ui/CustomDatePicker";
import {
    convertTextToHtml,
    formatBytes,
    getDynamicMaxHeightForAttachments,
    getDynamicMaxHeightForMessage,
    getImageInitials,
    getMailboxUserEmailText,
    getMessageTitleFromFolderType,
    getPageTitleFromFolderType,
    getRecipientsDisplayData,
    isFolderOwner,
} from "../../../helper/HelperFunctions";
import { FileFormatIcon } from "src/assets/images/svg/FileIconCollection";
import { getFileExtension } from "src/components/Compose/Helper/HelperFunctions";
import {
    DeliveryType,
    FolderType,
    MessageActions,
    MessageSource,
    MessageType,
    ShareFolderTypes,
} from "src/Core/Common/Enums";
import {
    DeleteIcon,
    DownloadIcon,
    ForwardIcon,
    QuickReplyIcon,
    ReplyAllIcon,
    ReplyIcon,
    VerticalEllipsisIcon,
} from "src/assets/images/svg/IconCollection";
import { InboxHeaderConstants, MessageToolbarConstants } from "src/Core/Common/Constants";
import { IReceivedMailMessage } from "src/Core/ViewModels/Message/ReceivedMailMessageModel";
import { IFileModel } from "src/Core/ViewModels/File/FileModel";
import { ISentMailMessage } from "src/Core/ViewModels/Message/SentMailMessageModel";
import { ISentMessageModel } from "src/Core/ViewModels/Message/SentMessageModel";
import { IFolderModel, IPagination } from "src/Core/ViewModels/Folder/FolderModel";
import { IReceivedMessageModel } from "src/Core/ViewModels/Message/ReceivedMessageModel";
import {
    deleteMessage as deleteRecipientMessage,
    deleteSharedMessage,
} from "../../../actionCreators/receivedMessageActionCreators";
import { deleteMessage as deleteSpamMessage } from "../../../actionCreators/spamMessageActionCreators";
import { AppNotifier } from "src/components/Common/Notification/AppNotifier";
import {
    deleteMessages as deleteSentMessage,
    fetchSentMessageDetailsHistory,
} from "src/actionCreators/sentMessageActionCreators";
import { ApplicationState } from "src/store";
import { IResourceLocator, ResourceIdLocators } from "../../../helper/ResourceIdLocators";
import { dataFormatter } from "src/components/reports/parts/Formatter";
import { ColorConstants } from "src/assets/custom/colors";
import { PrintComponent } from "src/components/Common/PrintComponent/PrintComponent";
import { MessagePrint } from "./MessagePrint";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { CustomModal } from "src/components/Common/Modal/CustomModal";
import { appInsightsData } from "src/helper/AppInsightsData";
import { mapToReceivedMessage } from "src/helper/MessageHelperThreads";

interface IMessageDetailProps {
    message: IReceivedMailMessage | ISentMailMessage | any;
    messageId: number;
    folderType: FolderType;
    pagination: IPagination;
    resourceLocators: IResourceLocator;
    folder?: IFolderModel;
    showQuickReply?: boolean;
    downloadSingleAttachment?: (
        fileId: number,
        physicalPath: string,
        messageId: number,
        fileName: string,
        senderEmail: string,
        receivedMessageGuid: string,
        messageSource: MessageSource,
        folderId: number
    ) => void;
    downloadAllAttachments?: (
        files: IFileModel[],
        messageId: number,
        senderEmail: string,
        receivedMessageGuid: string,
        messageSource: MessageSource,
        folderId: number
    ) => void;
    hideMessageDetailView?: (hide: boolean) => void;
    getMessageDetail?: (message: IReceivedMessageModel | ISentMessageModel) => void;
    changeExpiry?: (
        expiresDate: Date,
        message: IReceivedMessageModel | ISentMessageModel,
        callback?: (data: boolean) => void
    ) => void;
    fetchMessages: (pageNo: number, pageSize: number, search: string) => void;
    hideMessageDetail: (hide: boolean) => void;
    resetSelection: React.Dispatch<React.SetStateAction<boolean>>;
    resetSearchAndSort?: () => void;
    deleteFromArchive?: boolean;
    currentFolderId?: number;
}

const MessageDetail: React.FC<IMessageDetailProps> = props => {
    const [showHeaderOptions, toogleShowHeaderOptions] = useState(false);
    const [showDelete, toggleShowDelete] = useState(false);
    const [showSentMessageDelete, toggleShowSentMessageDelete] = useState(false);
    const [showExpiry, toggleShowExpiry] = useState(false);
    const [showDateValidation, toggleDateValidation] = useState(false);
    const [expiryDate, setExpiryDate] = useState(getCurrentDate());
    const { mailboxUserData } = useSelector((state: ApplicationState) => state.commonState);
    const clearDatePicker = React.useRef();
    const { resourceLocators } = props;
    const { messageOptions: messageOptionsSettings } = useSelector(
        (appState: ApplicationState) => appState.adminSettingsState
    );
    const changeExpiryDisabled =
        !messageOptionsSettings.retentionSettings?.isUserAllowedToChangeMessageAndDRRetentionPeriod ||
        props.folderType !== FolderType.INBOX ||
        props.message.messageDetail?.deliveryType === DeliveryType.QuickReply;
    const showReplyReplyAll = props.folderType !== FolderType.SPAM && !mailboxUserData;
    const showQuickReply = !mailboxUserData && props.showQuickReply && props.folderType == FolderType.INBOX;
    const [showMessageActivityPopover, setShowMessageActivityPopover] = useState(false);
    const activityLogToggle = useRef(null);
    const user = useSelector((state: ApplicationState) => state.userProfile);
    const dispatch = useDispatch();
    const { pagination, fetchMessages } = props;
    useEffect(() => {
        dispatch(fetchSentMessageDetailsHistory(props.messageId, MessageType.SENT));
    }, [props.messageId]);

    const sentDetails = useSelector((appState: ApplicationState) => appState.sentMessageState.mailData.activityLogData);
    const mailboxUserEmailId = getMailboxUserEmailText();
    const recipientEmailId = mailboxUserEmailId ? mailboxUserEmailId : user.emailAddress;
    const recipientExpiryDays =
        props.folderType === FolderType.SENT
            ? props.message?.messageDetail?.expiresDays
            : props.message?.recipients?.find((x: any) => x.email === recipientEmailId)?.expiresDays;

    const handleOnClickDelete = (e: any) => {
        e.stopPropagation();
        toggleShowDelete(true);
    };

    const handleOnHideDelete = () => {
        toggleShowDelete(false);
    };

    const handleOnClickSentMessageDelete = (e: any) => {
        e.stopPropagation();
        if (mailboxUserData) {
            toggleShowDelete(true);
        } else {
            toggleShowSentMessageDelete(true);
        }
    };

    const handleOnHideSentMessageDelete = () => {
        toggleShowSentMessageDelete(false);
    };

    const handleOnClickChangeExpiry = (e: any) => {
        if (props.folderType === FolderType.ARCHIVE || props.folderType === FolderType.SPAM) return;

        e.stopPropagation();
        toggleShowExpiry(true);
    };

    const hidePopup = () => {
        toggleShowExpiry(false);
        toggleDateValidation(false);
    };

    const handleOnSubmitDelete = () => {
        const callback = (result: any) => {
            if (result) {
                AppNotifier.Success(ToasterMessages.SUCCESS.DELETE_MESSAGES);
                toggleShowDelete(false);
                toggleShowSentMessageDelete(false);
                props?.resetSelection(true);
                props.hideMessageDetailView && props.hideMessageDetailView(true);
                props.resetSearchAndSort && props.resetSearchAndSort();
            }
        };
        switch (props.folderType) {
            case FolderType.ARCHIVE:
                dispatch(
                    deleteSharedMessage(
                        [props.message.messageDetail as IReceivedMessageModel],
                        callback,
                        getPageTitleFromFolderType(props.folderType),
                        resourceLocators.MsgDtlBtnDelete
                    )
                );
                break;
            case FolderType.INBOX:
                dispatch(
                    deleteRecipientMessage(
                        [props.message.messageDetail as IReceivedMessageModel],
                        callback,
                        getPageTitleFromFolderType(props.folderType),
                        resourceLocators.MsgDtlBtnDelete
                    )
                );
                break;
            case FolderType.SPAM:
                dispatch(
                    deleteSpamMessage(
                        [props.message.messageDetail as IReceivedMessageModel],
                        callback,
                        resourceLocators.MsgDtlBtnDelete
                    )
                );
                break;
            case FolderType.SENT:
                if (props.deleteFromArchive) {
                    dispatch(
                        deleteSharedMessage(
                            [props.message.messageDetail].map(mapToReceivedMessage),
                            callback,
                            appInsightsData.Folders.MyFiles.PageTitle,
                            ResourceIdLocators.MyFiles.MsgListBtnDelete
                        )
                    );
                } else {
                    dispatch(
                        deleteSentMessage(
                            [props.message.messageDetail as ISentMessageModel],
                            callback,
                            ResourceIdLocators.Sent.MsgDtlBtnDelete
                        )
                    );
                }
                break;
        }
    };

    const handleOnChangeExpiry = (e: any) => {
        const date = moment(e).toDate();
        const maxDate = new Date(
            getExpiryDateFromDays(props.message.messageDetail?.sentDate, props.message.messageDetail?.expiresDays)
        );
        if (date < getCurrentDate() || date > maxDate) {
            toggleDateValidation(true);
            setExpiryDate(getCurrentDate());
            clearDatePicker && clearDatePicker.current;
        } else {
            setExpiryDate(date);
            toggleDateValidation(false);
        }
    };

    const handleOnSubmitChangeExpiry = () => {
        if (showDateValidation) return;

        if (expiryDate.getTime() === getCurrentDate().getTime()) {
            AppNotifier.Warning(ToasterMessages.WARNING.REQUIRE_EXPIRY_DATE);
            return;
        }

        if (props.changeExpiry) {
            props.changeExpiry(expiryDate, props.message.messageDetail, (result: boolean) => {
                if (result) {
                    AppNotifier.Success(ToasterMessages.SUCCESS.CHANGE_EXPIRY);
                    toggleShowExpiry(false);
                    setExpiryDate(getCurrentDate());
                    props.getMessageDetail && props.getMessageDetail(props.message.messageDetail);
                }
            });
        }
    };

    /**Change Expiry Popover */
    const popover = (
        <Popover id="changeExpiry">
            <Popover.Header as="h3">Change Expiry</Popover.Header>
            <Popover.Body>
                <CustomDatePicker
                    minDate={getCurrentDate()}
                    maxDate={
                        new Date(
                            getExpiryDateFromDays(
                                props.message.messageDetail?.sentDate,
                                props.message.messageDetail?.expiresDays
                            )
                        )
                    }
                    clearDatePicker={clearDatePicker}
                    onSubmit={handleOnSubmitChangeExpiry}
                    onChange={handleOnChangeExpiry}
                />
                {showDateValidation && <p className="text-danger">Please enter a valid date</p>}
                <div className="change-expiry-footer">
                    <button className="button-grey" onClick={hidePopup}>
                        Cancel
                    </button>
                    <button className="btn-ok-change-expiry button-primary-blue-2" onClick={handleOnSubmitChangeExpiry}>
                        Ok
                    </button>
                </div>
            </Popover.Body>
        </Popover>
    );

    const handleOnClickReply = (messageActionType: MessageActions) => {
        //URL format: .../reply/messageActionType
        const url = `${window.location.origin}/reply/${messageActionType}/${props.folderType}`;
        Office.context.ui.displayDialogAsync(
            url,
            { height: messageActionType === MessageActions.QuickReply ? 66 : 80, width: 60, displayInIframe: true },
            asyncResult => {
                const dialog = asyncResult.value;
                dialog.addEventHandler(Office.EventType.DialogMessageReceived, (args: any) => {
                    if (args.message === "dialog-cancelled" && messageActionType === MessageActions.QuickReply) {
                        dialog.close();
                    } else {
                        props.hideMessageDetailView && props.hideMessageDetailView(true);
                        props.resetSearchAndSort && props.resetSearchAndSort();
                        dialog.close();
                    }
                });
                dialog.addEventHandler(Office.EventType.DialogEventReceived, args => {
                    dialog.close();
                });
            }
        );
    };
    const handleAttachmentClick = (
        item: any,
        senderEmail: string,
        receivedMessageGuid: string,
        messageSource: MessageSource
    ) => {
        props.downloadSingleAttachment &&
            props.downloadSingleAttachment(
                item.fileId,
                item.fileGuid,
                props.messageId,
                item.name,
                senderEmail,
                receivedMessageGuid,
                messageSource,
                props.message?.messageDetail?.folderId
            );
    };

    const handleDownloadAllClick = () => {
        props.folderType !== FolderType.SPAM &&
            props.downloadAllAttachments &&
            props.downloadAllAttachments(
                props.message.attachments,
                props.messageId,
                props.message.messageDetail.senderEmail,
                props.message.messageDetail?.receivedMessageGuid,
                props.message.messageDetail?.messageSource,
                props.message?.messageDetail?.folderId
            );
    };

    const DetailsPopover = (
        <Popover id="popover-activity">
            <Popover.Body className="message-detail-activity-popover">
                {sentDetails &&
                    Array.isArray(sentDetails) &&
                    sentDetails?.map(activityLogData => (
                        <>
                            <div key={activityLogData.email} className="activity">
                                <div className="main">
                                    <div className="email" title={activityLogData.email}>
                                        {activityLogData.email || "-"}
                                    </div>
                                    <div className="date">{dataFormatter(activityLogData.accessDate) || "-"}</div>
                                </div>
                                <div className="files">
                                    {activityLogData.filesDownloaded} / {activityLogData.filesUploaded}
                                </div>
                            </div>
                        </>
                    ))}
            </Popover.Body>
        </Popover>
    );
    const openPopover = () => {
        if (sentDetails.length !== 0) {
            setShowMessageActivityPopover(true);
        }
    };
    const enableEditPermission = (folder?: IFolderModel) => {
        if (
            !folder ||
            isFolderOwner(folder, user, mailboxUserData) ||
            folder.permissionGroupId == ShareFolderTypes.EDIT
        ) {
            return true;
        } else if (folder.permissionGroupId == ShareFolderTypes.VIEW) {
            return false;
        }
    };

    const isActionEnabled = (folder?: IFolderModel) => {
        return enableEditPermission(folder) && props.message.messageDetail.deliveryType !== DeliveryType.QuickReply;
    };

    const headerPopOver = (
        <Popover>
            <Popover.Body style={{ padding: "0" }} className="inbox-header-popover">
                <div className="flex-coloumn header-actions" onClick={() => toogleShowHeaderOptions(false)}>
                    {showQuickReply && (
                        <div
                            onClick={() => {
                                isActionEnabled(props.folder) && handleOnClickReply(MessageActions.QuickReply);
                            }}
                            title={InboxHeaderConstants.QUICK_REPLY}
                            className={`inbox-dd-wrapper ${
                                isActionEnabled(props.folder) ? "quick-reply-div" : "toolbar-icon-disabled"
                            } `}
                        >
                            <QuickReplyIcon />
                            <p>{InboxHeaderConstants.QUICK_REPLY}</p>
                        </div>
                    )}
                    <div
                        onClick={() => {
                            if (isActionEnabled(props.folder)) {
                                handleOnClickReply(MessageActions.Reply);
                            }
                        }}
                        data-resource-id={resourceLocators.MsgDtlBtnReply}
                        className={`inbox-dd-wrapper ${isActionEnabled(props.folder) ? "" : "toolbar-icon-disabled"}`}
                        title={InboxHeaderConstants.REPLY}
                    >
                        <ReplyIcon />
                        <p>{InboxHeaderConstants.REPLY}</p>
                    </div>

                    <div
                        onClick={() => {
                            if (isActionEnabled(props.folder)) {
                                handleOnClickReply(MessageActions.ReplyAll);
                            }
                        }}
                        data-resource-id={resourceLocators.MsgDtlBtnReplyAll}
                        className={`inbox-dd-wrapper ${isActionEnabled(props.folder) ? "" : "toolbar-icon-disabled"}`}
                        title={InboxHeaderConstants.REPLY_ALL}
                    >
                        <ReplyAllIcon />
                        <p>{InboxHeaderConstants.REPLY_ALL}</p>
                    </div>

                    <div
                        onClick={() => {
                            if (isActionEnabled(props.folder)) {
                                handleOnClickReply(MessageActions.Forward);
                            }
                        }}
                        data-resource-id={resourceLocators.MsgDtlBtnForward}
                        className={`inbox-dd-wrapper ${isActionEnabled(props.folder) ? "" : "toolbar-icon-disabled "}`}
                        title={InboxHeaderConstants.FORWARD}
                    >
                        <ForwardIcon />
                        <p>{InboxHeaderConstants.FORWARD}</p>
                    </div>

                    <div>
                        <PrintComponent showPrintIcon spamMessage={false} resourceId={resourceLocators.MsgDtlBtnPrint}>
                            <MessagePrint
                                message={props.message}
                                messageTypeText={getMessageTitleFromFolderType(props.folderType)}
                            />
                        </PrintComponent>
                    </div>
                </div>
            </Popover.Body>
        </Popover>
    );

    return (
        <>
            <div className="message-pane">
                <div className="message-pane-header flex-row-space-between">
                    <div className="flex-row">
                        <span className="inbox-btn" title="Back" onClick={() => props.hideMessageDetail(true)}>
                            <span className="fas fa-arrow-left"></span>
                        </span>
                    </div>
                    <div className="flex-row header-actions">
                        {(props.folderType == FolderType.SPAM || mailboxUserData !== null) && (
                            <span
                                className="inbox-btn"
                                style={{ cursor: "pointer" }}
                                title={MessageToolbarConstants.TITLE_PRINT}
                            >
                                <PrintComponent
                                    showPrintIcon
                                    spamMessage={true}
                                    resourceId={ResourceIdLocators.Spam.MsgDtlBtnPrint}
                                >
                                    <MessagePrint
                                        message={props.message}
                                        messageTypeText={getMessageTitleFromFolderType(props.folderType)}
                                    />
                                </PrintComponent>
                            </span>
                        )}
                        {enableEditPermission(props.folder) ? (
                            <span
                                onClick={
                                    props.folderType !== FolderType.SENT
                                        ? handleOnClickDelete
                                        : handleOnClickSentMessageDelete
                                }
                                data-resource-id={resourceLocators.MsgDtlBtnDelete}
                                className="inbox-btn"
                                title={MessageToolbarConstants.TITLE_DELETE}
                            >
                                <DeleteIcon width={16} height={16} />
                            </span>
                        ) : (
                            <span
                                data-resource-id={resourceLocators.MsgDtlBtnDelete}
                                className="inbox-btn toolbar-icon-disabled"
                                title={MessageToolbarConstants.TITLE_DELETE}
                            >
                                <DeleteIcon width={16} height={16} />
                            </span>
                        )}

                        {props.message.attachments && props.message.attachments.length > 0 && (
                            <span
                                className={`inbox-btn download-button ${
                                    props.folderType === FolderType.SPAM &&
                                    "disabled-pointer attachment-disabled toolbar-icon-disabled"
                                }`}
                                title={InboxHeaderConstants.DOWNLOAD}
                                data-resource-id={resourceLocators.MsgDtlBtnDownloadFiles}
                                onClick={props.message.attachments.length > 0 && handleDownloadAllClick}
                            >
                                <DownloadIcon
                                    disabled={
                                        props.message.attachments.length === 0 || props.folderType === FolderType.SPAM
                                    }
                                    width={16}
                                    height={16}
                                />
                                <span
                                    className={`attachment-count ${
                                        props.folderType === FolderType.SPAM &&
                                        "disabled-pointer attachment-count-disabled"
                                    }`}
                                >
                                    {props.message.attachments.length}
                                </span>
                            </span>
                        )}

                        {/*To do-- Additional icons based on Inbox or spam to be added  */}

                        {showReplyReplyAll && (
                            <OverlayTrigger
                                show={showHeaderOptions}
                                trigger="click"
                                placement="bottom-end"
                                overlay={headerPopOver}
                                rootClose
                                onToggle={() => toogleShowHeaderOptions(!showHeaderOptions)}
                            >
                                <span
                                    onClick={() => {
                                        toogleShowHeaderOptions(!showHeaderOptions);
                                    }}
                                    className="inbox-btn ellipsis-icon"
                                    title={MessageToolbarConstants.ELLIPSIS_TITLE}
                                >
                                    <VerticalEllipsisIcon width={18} height={18} color="#606060" />
                                </span>
                            </OverlayTrigger>
                        )}
                    </div>
                </div>
                <div className="message-pane__body">
                    <div
                        title={props.message.messageDetail.subject || "(no subject)"}
                        className="message-pane__subject truncate"
                    >
                        {props.message.messageDetail.subject || "(no subject)"}
                    </div>
                    <div className="message-pane__body-top">
                        <div className="flex-row-space-between">
                            <div className="flex-row w-100">
                                <div className="avatar-wrapper">
                                    <div className="avatar">
                                        {getImageInitials(
                                            props.message.messageDetail.senderFirstName,
                                            props.message.messageDetail.senderLastName
                                        )}
                                    </div>
                                </div>
                                <div className="flex-coloumn message-sender-wrapper">
                                    <div className="d-flex align-items-center w-100">
                                        <span
                                            className="message-pane__sender ellipsis"
                                            title={`${props.message.messageDetail.senderFirstName} ${props.message.messageDetail.senderLastName}`}
                                        >
                                            {`${props.message.messageDetail.senderFirstName} ${props.message.messageDetail.senderLastName}`}
                                        </span>

                                        <div
                                            className="message-pane__expiredate"
                                            title={
                                                changeExpiryDisabled
                                                    ? props.folderType === FolderType.INBOX
                                                        ? MessageToolbarConstants.TITLE_CHANGE_EXPIRY_RESTRICTED
                                                        : ""
                                                    : MessageToolbarConstants.TITLE_CHANGE_EXPIRY
                                            }
                                        >
                                            <OverlayTrigger
                                                show={showExpiry}
                                                trigger="click"
                                                placement="bottom-start"
                                                overlay={popover}
                                                rootClose
                                                onToggle={() => toggleShowExpiry(!showExpiry)}
                                            >
                                                <span
                                                    className={`fas fa-exclamation-circle ${
                                                        changeExpiryDisabled ? "cursor-disabled" : "cursor-pointer"
                                                    }`}
                                                    data-resource-id={resourceLocators.MsgDtlBtnChangeExpiry}
                                                    onClick={e => handleOnClickChangeExpiry(e)}
                                                ></span>
                                            </OverlayTrigger>
                                            <span>
                                                &nbsp; Expires on{" "}
                                                {getExpiryDateFromDays(
                                                    props.message.messageDetail.sentDate,
                                                    recipientExpiryDays
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="message-pane__sentdate">
                                        {getFormattedSentDateForDetailedView(props.message.messageDetail.sentDate)}
                                    </div>
                                    <div className="message-pane__recipient">
                                        <b>
                                            To
                                            <Overlay
                                                target={activityLogToggle.current}
                                                placement="bottom-start"
                                                show={showMessageActivityPopover}
                                                rootClose
                                                onHide={() => setShowMessageActivityPopover(false)}
                                            >
                                                {DetailsPopover}
                                            </Overlay>
                                            {props.folderType == FolderType.SENT ? (
                                                <span
                                                    className="fa fa-caret-down"
                                                    style={{ color: ColorConstants.grey, paddingLeft: "2px" }}
                                                    title="Download Info"
                                                    ref={activityLogToggle}
                                                    onClick={openPopover}
                                                ></span>
                                            ) : null}
                                            :
                                        </b>
                                        &nbsp;
                                        {props.message.recipients.map((item, i) => {
                                            return (
                                                <React.Fragment key={i}>
                                                    {getRecipientsDisplayData(
                                                        item.firstName,
                                                        item.lastName,
                                                        item.email
                                                    )}
                                                    {i !== props.message.recipients.length - 1 && <span>, </span>}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                    {(props.folderType == FolderType.SENT || props.folderType == FolderType.INBOX || props.folderType == FolderType.ARCHIVE ||
                                       props.folderType == FolderType.SPAM) ? (
                                      <div className="message-pane-recipient">
                                        <b> Tax Year: </b> &nbsp;
                                            <span>
                                             {props.message.messageDetail.taxYear === 0 || !props.message.messageDetail.taxYear
                                                ? "NA" : props.message.messageDetail.taxYear}
                                        </span>
                                         </div> ) : null
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="message-pane__message"
                        style={{
                            borderBottomStyle: `${props.message.messageDetail.message.length > 200 ? "solid" : "none"}`,
                            maxHeight: `${getDynamicMaxHeightForMessage(
                                props.message.attachments.length,
                                props.message.messageDetail.message.length
                            )}`,
                        }}
                    >
                        <div
                            dangerouslySetInnerHTML={{
                                __html: convertTextToHtml(props.message.messageDetail.message),
                            }}
                        ></div>
                    </div>
                </div>

                {props.message.attachments && props.message.attachments.length > 0 && (
                    <div
                        className={`message-pane__attachment  ${
                            props.folderType === FolderType.SPAM && "spam-disabled"
                        }`}
                    >
                        <div
                            className="attachments"
                            style={{
                                maxHeight: `${getDynamicMaxHeightForAttachments(
                                    props.message.attachments.length,
                                    props.message.messageDetail.message.length
                                )}`,
                            }}
                        >
                            {props.message.attachments.map((item, i) => {
                                return (
                                    <div
                                        className={`attachment-card  ${
                                            props.folderType === FolderType.SPAM && "attachment-disabled"
                                        }`}
                                        key={`file${i}`}
                                        data-resource-id={resourceLocators.MsgDtlBtnDownloadFile}
                                        onClick={() =>
                                            props.folderType !== FolderType.SPAM &&
                                            handleAttachmentClick(
                                                item,
                                                props.message?.messageDetail?.senderEmail,
                                                props.message?.messageDetail?.receivedMessageGuid,
                                                props.message.messageDetail?.messageSource
                                            )
                                        }
                                        style={{ width: "48%" }}
                                    >
                                        <div className="attachment-icon">
                                            <FileFormatIcon
                                                height={40}
                                                width={35}
                                                disabled={props.folderType === FolderType.SPAM}
                                                extension={getFileExtension(item.name)}
                                            ></FileFormatIcon>
                                        </div>
                                        <div
                                            className="attachment-detail ellipsis"
                                            title={item.name}
                                            style={{ width: "80%" }}
                                        >
                                            {item.name}
                                            <div>{formatBytes(item.size)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <CustomModal
                className="delete-message-modal"
                show={showDelete}
                title={MessageToolbarConstants.TITLE_DELETE_POPUP}
                cancelButtonName={MessageToolbarConstants.TITLE_CANCEL}
                confirmButtonName={MessageToolbarConstants.TITLE_DELETE}
                onSubmit={handleOnSubmitDelete}
                onHide={handleOnHideDelete}
                isConfirmModal={true}
            >
                <span>{MessageToolbarConstants.DELETE_SINGLE_MESSAGE}</span>
            </CustomModal>

            <CustomModal
                show={showSentMessageDelete}
                title={MessageToolbarConstants.TITLE_DELETE_POPUP}
                cancelButtonName={MessageToolbarConstants.TITLE_CANCEL}
                confirmButtonName={MessageToolbarConstants.TITLE_DELETE}
                onSubmit={handleOnSubmitDelete}
                onHide={handleOnHideSentMessageDelete}
                isConfirmModal={true}
            >
                <span>{MessageToolbarConstants.DELETE_SINGLE_MESSAGE}</span>
            </CustomModal>
        </>
    );
};

export default MessageDetail;
